import { AfterViewInit, Component, HostListener, OnInit, Renderer2 } from '@angular/core';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { Title } from "@angular/platform-browser";
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { MenuController, ModalController } from '@ionic/angular';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { AnimationOptions } from 'ngx-lottie';
import { filter } from "rxjs/operators";
import { UserService } from 'src/app/shared-lib/domains/user';
import { register } from 'swiper/element/bundle';
import { environment } from '../environments/environment';
import { AppCoreService } from './core/app-core.service';
import { AuthService } from './core/auth.service';
import { loadJsScript } from './core/utils';
import { AcceptanceFlows } from './flows/acceptance.flows';
import { AppFlows } from './flows/app.flows';
import { AuthFlows } from './flows/auth.flows';
import { UserFlows } from './flows/user.flows';
import { WalletFlows } from './flows/wallet.flows';
import { AnalyticsService } from './shared/analytics.service';
import { AppService } from './shared/app.service';
import { CartService } from './shared/cart.service';
import { MetaTagsService } from "./shared/meta-tags.service";
import { OrderStateService } from './shared/order-state.service';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {
  version = environment.version;
  loaderAnimationOptions: AnimationOptions = {
    path: '/assets/lotties/animacionLogoCopyfly.json',
    autoplay: true,
    loop: true,
  };

  @HostListener('window:resize')
  onWindowResize() {

  }

  public user: any;
  showFaqSidebar = false;

  constructor(
    private analytics: Analytics,
    private authService: AuthService,
    public userService: UserService,
    public router: Router,
    public modalController: ModalController,
    private gtmService: GoogleTagManagerService,
    private GTAnalytics: AnalyticsService,
    private AcceptanceFlow: AcceptanceFlows,
    public cartService: CartService,
    private orderStateService: OrderStateService,
    public appCoreService: AppCoreService,
    public appService: AppService,
    private menuCtrl: MenuController,
    private metaTagsService: MetaTagsService,
    private titleService: Title,
    private renderer: Renderer2,
    private authFlow: AuthFlows,
    private walletFlow: WalletFlows,
    private userFlows: UserFlows,
    private appFlows: AppFlows
  ) {
    loadJsScript(environment.redsysScript);
    this.initCookieConsent();

    this.gtmService.addGtmToDom();
    this.addMapsApiToDom();
    this.walletFlow.init();
    this.userFlows.init();
    this.appFlows.init();
    this.initializeApp();
    logEvent(this.analytics, 'app_open', { component: 'AppComponent' });
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      if (environment.production) {
        this.handleRouteChangeMeta();
      }
    });
  }

  initCookieConsent() {
    if (environment.production) {
      let script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = "https://cdn.cookie-script.com/s/12646ef1e2c6919ecef016e8621c9320.js";
      document.getElementsByTagName('head')[0].appendChild(script);
    }
  }

  async initializeApp() {
    this.userService.user$.subscribe(user => this.normalizeUser(user));
  }

  async normalizeUser(user) {

    if (user && user.id && !user.acceptance?.privacy && !this.justSignIn()) {
      if (await this.authFlow.presentAcceptance(true)) {
        const respId = this.AcceptanceFlow.userAcceptance(user);
        if (respId) {
          this.userService.setUser({
            ...user,
            acceptance: {
              ...user.acceptance,
              privacy: true,
              tos: true
            }
          });
        }
      } else {
        this.doLogout();
      }
    }
  }

  justSignIn() {
    const today = new Date() as any;
    const lastSignIn = new Date(this.authService.authState.metadata.lastSignInTime) as any;
    const diffTime = Math.abs(today - lastSignIn);
    return (diffTime < (1000 * 10));
  }

  ngOnInit() {

  }

  ngAfterViewInit() {

  }

  async doLogout() {
    await this.authService.doLogout();
    this.router.navigate(['']);
    await this.orderStateService.resetOrderAsync();
    setTimeout(() => window.location.reload(), 1);
  }

  isDevelopmentContext() {
    return !environment.production;
  }

  closeMenu() {
    this.menuCtrl.close();
  }

  private handleRouteChangeMeta() {
    // Aquí puedes poner lógica para determinar qué meta tags deberían ser activadas
    // basado en la ruta actual, por ejemplo:
    const url = this.router.url;
    if (url === '/') {
      this.metaTagsService.updateMetaTags([
        {
          name: 'description',
          content: 'Imprime online en minutos con Copyfly. Sube tus archivos, elige opciones y recíbelos en casa en 24 h, fácil y sin complicaciones'
        },
        { name: 'robots', content: 'index, follow' }
      ]);
      this.titleService.setTitle('Imprime online y recibe en un fly');
    } else if (url.startsWith('/papeleria') || url.startsWith('/tienda')) {
      this.metaTagsService.updateMetaTags([
        {
          name: 'description',
          content: 'Libretas con papeles de colores, pósits transparentes pasteles, subrayador negro Stabilo y mucho más, envíos en 24 horas, entra en la papelería online más aesthetics. '
        },
        { name: 'robots', content: 'index, follow' }
      ]);
      this.titleService.setTitle('La papelería online más aesthetics | Envíos en 24 horas ');
    } else {
      this.metaTagsService.updateMetaTags([
        {
          name: 'description',
          content: 'Imprime online y recibe en un fly por 0,01 BN y 0,06 C, usando el cupón CFLY con un 5% dto. Envíos gratis a partir de 35 €. '
        },
        { name: 'robots', content: 'noindex' }
      ]);
      this.titleService.setTitle('Imprime online y recibe en un fly | 0,01 BN y 0,06 C | Usa CFLY 5% dto.');
    }
  }

  private addMapsApiToDom() {
    const existingScript = document.getElementById('google-maps-api-script');
    if (existingScript) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-api-script';
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.firebaseConfig.apiKey}&loading=async`;
    const head = document.getElementsByTagName('head')[0];
    if (head !== null && head !== undefined) {
      document.head.appendChild(script);
    }
  }

  toStationery() {
    let queryParams = { o: 'relevance', d: 'desc' };
    let navigationExtras: NavigationExtras = {
      queryParams
    };
    this.router.navigate(['papeleria/catalogo'], navigationExtras);
  }

  abrirSidebarFaq(show: boolean) {
    this.showFaqSidebar = show;
  }
}

